import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { LinkTarget } from '@/dap-design-system'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './command-item.scss?inline'

/**
 * `dap-ds-command-item`
 * @summary A command item is a command item that can be selected.
 * @element dap-ds-command-item
 * @title - Command item
 * @group command
 *
 * @event {{ value: string, closeOnSelect: boolean }} dds-command-item-click - Fired when the command item is clicked.
 *
 * @slot - The label of the command item.
 * @slot check - The check of the command item.
 *
 * @csspart base - The base part of the command item.
 * @csspart content - The content part of the command item.
 * @csspart label - The label part of the command item.
 *
 * @cssprop --dds-command-item-padding - The padding of the command item
 * @cssprop --dds-command-item-background - The background color of the command item
 * @cssprop --dds-command-item-color - The text color of the command item
 * @cssprop --dds-command-item-hover-background - The background color of the command item on hover
 * @cssprop --dds-command-item-selected-background - The background color of the selected command item
 */
@customElement('dap-ds-command-item')
export default class DapDSCommandItem extends DdsElement {
  @query('#trigger')
  private readonly _trigger!: HTMLButtonElement

  static readonly styles = unsafeCSS(styles)

  /**
   * The value of the command item.
   */
  @property({ type: String })
  value?: string

  /**
   * Whether the command item is disabled.
   */
  @property({ type: Boolean })
  disabled = false

  /** The name of the button */
  @property() name: string

  /** The Href of the button. If this present the button will be rendered as an anchor `<a></a>` element. */
  @property() href: string

  /** The target of the button
   * @type { '_blank' | '_self' | '_parent' | '_top' }
   */
  @property() target: LinkTarget = '_self'

  /**
   * The rel of the button link. Default is `noreferrer noopener`.
   */
  @property() rel = 'noreferrer noopener'

  /**
   * Whether the command item is selectable.
   */
  @property({ type: Boolean })
  selectable = false

  /**
   * Whether the command item is selected.
   */
  @property({ type: Boolean })
  selected = false

  /**
   * Whether the command item should close the command when selected.
   */
  @property({ type: String })
  closeOnSelect = 'true'

  @state()
  private _isSelected = false

  connectedCallback() {
    super.connectedCallback()
    this._isSelected = this.selected
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('selected')) {
      this._isSelected = this.selected
    }
  }

  private _handleClick(event: Event) {
    event.stopPropagation()

    if (this.disabled) return
    this._isSelected = !this._isSelected
    this.emit('dds-command-item-click', {
      value: this.value,
      closeOnSelect: this.closeOnSelect,
    })
  }

  private _handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!this.href) {
        event.preventDefault()
        this._handleClick(event)
      } else {
        event.stopPropagation()
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      this.emit('dds-close')
    }
  }

  public focus() {
    if (this._trigger) {
      this._trigger.focus()
    }
  }

  render() {
    return html`
      <${unsafeStatic(this.href ? 'a' : 'button')}
        part="base"
        id="trigger"
        class=${clsx('command-item', {
          'command-item--disabled': this.disabled,
          'command-item--selected': this.selectable && this._isSelected,
        })}
        .disabled=${this.disabled}
        aria-disabled=${this.disabled}
        aria-label=${ifDefined(this.ariaLabel) || ''}
        aria-pressed=${ifDefined(this.href ? undefined : this.selected)}
        href=${this.href}
        target=${this.target}
        rel=${this.rel}
        .autofocus=${this.autofocus}
        @click=${this._handleClick}
        @keydown=${this._handleKeyDown}>
        <span
          part="content"
          class=${clsx('command-item__content', {
            'command-item__content--selectable': this.selectable,
          })}>
          ${
            this.selectable
              ? this._isSelected
                ? html`<slot name="check"
                    ><dap-ds-icon
                      name="check-line2"
                      aria-hidden="true"></dap-ds-icon
                  ></slot>`
                : html`<span class="sr-only"></span>`
              : nothing
          }
          <span part="label" class="command-item__label"><slot></slot></span>
        </span>
      </${unsafeStatic(this.href ? 'a' : 'button')}
    `
  }
}
